import { gql, useMutation, useQuery } from '@apollo/client';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { PageHeader } from '../../components/PageHeader';

type JsonValue = any;

type AppConfig = {
  id: number;
  key: string;
  data: JsonValue;
  archived: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type AppConfigsQueryData = {
  appConfigs: AppConfig[];
};

type UpdateAppConfigResponse = {
  updateAppConfig: AppConfig;
};

type UpdateAppConfigVariables = {
  input: {
    key: string;
    data: JsonValue;
    archived?: boolean;
  };
};

type JsonParseResult = {
  data: JsonValue;
  error?: undefined;
} | {
  data?: undefined;
  error: string;
};

type StructuredEditorProps = {
  data: JsonValue;
  onSetValue: (path: string[], value: JsonValue) => void;
  onReplaceData?: (data: JsonValue) => void;
};

type CampaignChannel = 'push' | 'email';

type CampaignTemplateDefinition = {
  key: string;
  channel: CampaignChannel;
  title: string;
  description: string;
  defaultTitle: string;
  defaultText: string;
};

type CampaignTemplateGroup = {
  key: string;
  title: string;
  description: string;
  templates: CampaignTemplateDefinition[];
};

type EmailLanguageOption = {
  code: string;
  label: string;
};

type LocalizedEmailEditorProps = {
  notificationKey: string;
  channel: Record<string, JsonValue>;
  languages: EmailLanguageOption[];
  onSetValue: (path: string[], value: JsonValue) => void;
};

type AppConfigHashState = {
  configKey?: string;
  notificationKey?: string;
};

type NotificationPreviewOption = {
  id: string;
  label: string;
  title: JsonValue;
  text: JsonValue;
};

const EMAIL_ROLE_OPTIONS = [
  { value: 'default', label: 'Coach / default' },
  { value: 'Coach', label: 'Coach' },
  { value: 'Player', label: 'Player' },
  { value: 'TeamAdminParent', label: 'Parent / Team Admin' },
  { value: 'Fan', label: 'Fan' },
];

const EMAIL_ROLE_KEYS = new Set(EMAIL_ROLE_OPTIONS.map((option) => option.value));
const FALLBACK_EMAIL_LANGUAGES = ['en', 'uk', 'es', 'fr', 'pt'];
const COMMON_EMAIL_VARIABLES = ['firstName', 'lastName', 'deepLink', 'unsubscribeUrl'];

const APP_CONFIGS_QUERY = gql`
  query AdminAppConfigs {
    appConfigs {
      id
      key
      data
      archived
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_APP_CONFIG_MUTATION = gql`
  mutation UpdateAppConfig($input: AppConfigUpdateInput!) {
    updateAppConfig(input: $input) {
      id
      key
      data
      archived
      createdAt
      updatedAt
    }
  }
`;

const isRecord = (value: JsonValue): value is Record<string, JsonValue> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const readAppConfigHash = (): AppConfigHashState => {
  const segments = window.location.hash
    .replace(/^#\/?/, '')
    .split('/')
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment));

  return {
    configKey: segments[0],
    notificationKey: segments[0] === 'notification' ? segments[1] : undefined,
  };
};

const writeAppConfigHash = (
  configKey: string,
  notificationKey?: string,
  replace = false,
) => {
  const segments = [configKey, notificationKey]
    .filter((segment): segment is string => Boolean(segment))
    .map((segment) => encodeURIComponent(segment));
  const hash = `#/${segments.join('/')}`;

  if (window.location.hash === hash) return;

  if (replace) {
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}${hash}`,
    );
    return;
  }

  window.location.hash = `/${segments.join('/')}`;
};

const formatJson = (data: JsonValue): string => {
  const formatted = JSON.stringify(data ?? null, null, 2);
  return typeof formatted === 'string' ? formatted : 'null';
};

const parseJson = (source: string): JsonParseResult => {
  try {
    return { data: JSON.parse(source) };
  } catch (error: unknown) {
    return { error: error instanceof Error ? error.message : String(error) };
  }
};

const cloneJson = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

const adminCardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 'none',
  textAlign: 'left',
};

const adminCardHeaderStyle: React.CSSProperties = {
  background: '#ffffff',
  color: 'inherit',
  borderBottom: '1px solid rgba(0, 0, 0, 0.125)',
};

const getPayloadSize = (data: JsonValue): string => {
  const raw = JSON.stringify(data ?? null) || '';
  if (raw.length < 1024) return `${raw.length} B`;
  return `${(raw.length / 1024).toFixed(1)} KB`;
};

const getDataType = (data: JsonValue): string => {
  if (Array.isArray(data)) return 'array';
  if (data === null) return 'null';
  return typeof data;
};

const formatDate = (value?: string | null): string => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const humanizeKey = (value: string): string =>
  value
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const truncate = (value: JsonValue, maxLength = 90): string => {
  if (value === undefined || value === null) return '';
  const text = String(value);
  return text.length > maxLength ? `${text.slice(0, maxLength - 3)}...` : text;
};

const stripHtml = (value: JsonValue): string => {
  if (value === undefined || value === null) return '';
  return String(value)
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const hasNotificationShape = (data: JsonValue): boolean => {
  if (!isRecord(data)) return false;
  return Object.keys(data).some((key) => {
    const item = data[key];
    return isRecord(item) && (isRecord(item.push) || isRecord(item.email));
  });
};

const validateLocalizedEmailContent = (data: JsonValue): string[] => {
  if (!isRecord(data)) return [];
  const errors: string[] = [];

  Object.entries(data).forEach(([notificationKey, entry]) => {
    if (!isRecord(entry) || !isRecord(entry.email) || !isRecord(entry.email.content)) return;

    Object.entries(entry.email.content).forEach(([role, localizedByLanguage]) => {
      if (!EMAIL_ROLE_KEYS.has(role)) {
        errors.push(`${notificationKey}: unsupported email role "${role}"`);
      }
      if (!isRecord(localizedByLanguage)) {
        errors.push(`${notificationKey}/${role}: localized content must be an object`);
        return;
      }

      Object.entries(localizedByLanguage).forEach(([language, localized]) => {
        if (!/^[a-z]{2,3}$/i.test(language)) {
          errors.push(`${notificationKey}/${role}: invalid language code "${language}"`);
        }
        if (!isRecord(localized)) {
          errors.push(`${notificationKey}/${role}/${language}: localization must be an object`);
          return;
        }
        if (!String(localized.subject || '').trim()) {
          errors.push(`${notificationKey}/${role}/${language}: subject is required`);
        }
        if (!String(localized.html || localized.text || '').trim()) {
          errors.push(`${notificationKey}/${role}/${language}: HTML or plain text body is required`);
        }
      });
    });
  });

  return errors;
};

const setValueAtPath = (target: JsonValue, path: string[], value: JsonValue): boolean => {
  if (!isRecord(target) || path.length === 0) return false;

  let cursor: Record<string, JsonValue> = target;
  for (let index = 0; index < path.length - 1; index += 1) {
    const key = path[index];
    if (!isRecord(cursor[key])) {
      cursor[key] = {};
    }
    cursor = cursor[key];
  }

  cursor[path[path.length - 1]] = value;
  return true;
};

const collectRuleTargets = (rules: JsonValue): string[] => {
  if (!isRecord(rules)) return [];

  return Object.keys(rules).reduce<string[]>((targets, ruleKey) => {
    const rule = rules[ruleKey];
    if (!isRecord(rule)) return targets;

    Object.keys(rule).forEach((targetKey) => {
      if (targets.indexOf(targetKey) === -1) {
        targets.push(targetKey);
      }
    });

    return targets;
  }, []);
};

const POLICY_CORE_FIELDS = ['enabled', 'notifyActor', 'channels', 'rules', 'minorHandling'];

const POLICY_DESCRIPTIONS: Record<string, string> = {
  attendanceStatusChanged: 'Attendance response notifications for coaches, players, and linked parents.',
  parentControlChanged: 'Parent-control link, unlink, and removal notifications.',
  lineupInstructionsShared: 'Lineup instruction sharing notifications.',
  lineupPublished: 'Match lineup publication notifications.',
  teamFeedPublished: 'Team feed publication notifications for practice results, match events, and game results.',
  practiceEvaluationSaved: 'Practice evaluation result notifications.',
};

const isEditablePolicySetting = (value: JsonValue): boolean => (
  typeof value === 'boolean' || typeof value === 'string' || typeof value === 'number'
);

const CAMPAIGN_TEMPLATE_GROUPS: CampaignTemplateGroup[] = [
  {
    key: 'new-signup',
    title: 'New Signup',
    description: 'Coach onboarding after signup, team creation, first invites, and first event.',
    templates: [
      {
        key: 'Campaign_NewSignup_WelcomePush',
        channel: 'push',
        title: 'Welcome push',
        description: 'Sent immediately after account creation.',
        defaultTitle: 'Welcome to Ultrain',
        defaultText: 'Create your first team and start managing your season.',
      },
      {
        key: 'Campaign_NewSignup_WelcomeEmail',
        channel: 'email',
        title: 'Welcome email',
        description: 'Sent shortly after signup.',
        defaultTitle: 'Your team workspace is ready',
        defaultText: 'Set up your team, invite players, and schedule the first event in Ultrain.',
      },
      {
        key: 'Campaign_NewSignup_NoTeam24hPush',
        channel: 'push',
        title: 'No team 24h push',
        description: 'Nudges a new coach to create the first team.',
        defaultTitle: 'Create your first team',
        defaultText: 'Set up your roster so players and parents have one place for updates.',
      },
      {
        key: 'Campaign_NewSignup_NoTeam48hEmail',
        channel: 'email',
        title: 'No team 48h email',
        description: 'Explains the value of creating the first team.',
        defaultTitle: 'Create your first team in minutes',
        defaultText: 'Add your team in Ultrain so schedules, invites, and updates stay connected.',
      },
      {
        key: 'Campaign_NewSignup_InvitePlayer72hPush',
        channel: 'push',
        title: 'Invite player push',
        description: 'Sent after team creation when no players have joined yet.',
        defaultTitle: 'Invite your players',
        defaultText: 'Share the invite link and get your roster started.',
      },
      {
        key: 'Campaign_NewSignup_SchedulePracticeDay5Email',
        channel: 'email',
        title: 'Schedule practice email',
        description: 'Feature discovery email for scheduling the first practice.',
        defaultTitle: 'Put the first practice on the calendar',
        defaultText: 'Create an event so every player knows what is next.',
      },
      {
        key: 'Campaign_NewSignup_FirstWeekRecapPush',
        channel: 'push',
        title: 'First week recap push',
        description: 'Sent one week after signup.',
        defaultTitle: 'Your first week on Ultrain',
        defaultText: 'Review your setup and finish the next step for your team.',
      },
    ],
  },
  {
    key: 'email-lead',
    title: 'Email Lead',
    description: 'Website leads who left email but have not converted.',
    templates: [
      {
        key: 'Campaign_EmailLead_ImmediateEmail',
        channel: 'email',
        title: 'Immediate email',
        description: 'First email after the website lead is captured.',
        defaultTitle: 'See what Ultrain can do for your team',
        defaultText: 'Create your account to manage schedules, invites, and team updates in one place.',
      },
      {
        key: 'Campaign_EmailLead_NoDownloadDay3Email',
        channel: 'email',
        title: 'No download day 3',
        description: 'Follow-up for a lead with no tracked download by day 3.',
        defaultTitle: 'Ready to organize your team?',
        defaultText: 'Ultrain keeps rosters, practices, games, and communication together.',
      },
      {
        key: 'Campaign_EmailLead_NoDownloadDay7Email',
        channel: 'email',
        title: 'No download day 7',
        description: 'Second nurture email for the website lead funnel.',
        defaultTitle: 'Bring your team onto Ultrain',
        defaultText: 'Start with one team, invite players, and schedule the first event in minutes.',
      },
      {
        key: 'Campaign_EmailLead_NoDownloadDay14Email',
        channel: 'email',
        title: 'No download day 14',
        description: 'Last nurture email in the current website lead plan.',
        defaultTitle: 'Still interested in Ultrain?',
        defaultText: 'Your team can be set up quickly whenever you are ready.',
      },
    ],
  },
  {
    key: 'install-no-signup',
    title: 'Install No Signup',
    description: 'Anonymous app installs that have not completed signup.',
    templates: [
      {
        key: 'Campaign_InstallNoSignup_2hPush',
        channel: 'push',
        title: '2h push',
        description: 'First reminder after install without signup.',
        defaultTitle: 'Finish setting up Ultrain',
        defaultText: 'Create your account and get your team workspace ready.',
      },
      {
        key: 'Campaign_InstallNoSignup_24hPush',
        channel: 'push',
        title: '24h push',
        description: 'Second anonymous install reminder.',
        defaultTitle: 'Your team tools are waiting',
        defaultText: 'Sign up to create a roster, schedule events, and share updates.',
      },
      {
        key: 'Campaign_InstallNoSignup_72hPush',
        channel: 'push',
        title: '72h push',
        description: 'Final anonymous install reminder.',
        defaultTitle: 'Start your season setup',
        defaultText: 'Open Ultrain and create your account when you are ready.',
      },
    ],
  },
  {
    key: 'dormant',
    title: 'Dormant',
    description: 'Re-engagement for coaches who have stopped opening the app.',
    templates: [
      {
        key: 'Campaign_Dormant_Day14Push',
        channel: 'push',
        title: 'Day 14 push',
        description: 'First re-engagement push.',
        defaultTitle: 'Check your team',
        defaultText: 'Open Ultrain to review your schedule, roster, and latest updates.',
      },
      {
        key: 'Campaign_Dormant_Day21Email',
        channel: 'email',
        title: 'Day 21 email',
        description: 'Email re-engagement after continued inactivity.',
        defaultTitle: 'Your team workflow is waiting',
        defaultText: 'Jump back into Ultrain to keep players, parents, and your schedule up to date.',
      },
      {
        key: 'Campaign_Dormant_Day30Push',
        channel: 'push',
        title: 'Day 30 push',
        description: 'Final dormant-user push in the current plan.',
        defaultTitle: 'Still using Ultrain?',
        defaultText: 'Open the app to catch up on your team and season activity.',
      },
    ],
  },
  {
    key: 'active',
    title: 'Active Milestones',
    description: 'Feature adoption and referral nudges for active coaches.',
    templates: [
      {
        key: 'Campaign_Active_FirstGameShareLineupPush',
        channel: 'push',
        title: 'First game lineup push',
        description: 'Sent when the first game is scheduled.',
        defaultTitle: 'Share your lineup',
        defaultText: 'Your first game is scheduled. Build or share the lineup so your team is ready.',
      },
      {
        key: 'Campaign_Active_ThreeGamesReferralEmail',
        channel: 'email',
        title: 'Three games referral email',
        description: 'Referral prompt after a coach has managed three games.',
        defaultTitle: 'Know another coach who could use Ultrain?',
        defaultText: 'You have managed three games in Ultrain. Share it with another coach who wants a simpler workflow.',
      },
      {
        key: 'Campaign_Active_FullSquadShareTeamPagePush',
        channel: 'push',
        title: 'Full squad team page push',
        description: 'Sent when the team reaches a full squad.',
        defaultTitle: 'Your roster is ready',
        defaultText: 'Share the team page so players and parents stay aligned.',
      },
      {
        key: 'Campaign_Active_MonthlyRecapEmail',
        channel: 'email',
        title: 'Monthly recap email',
        description: 'Monthly recap for coaches with sufficient team activity.',
        defaultTitle: 'Your monthly team recap',
        defaultText: 'Review your team activity from last month and keep your season moving.',
      },
    ],
  },
];

const CAMPAIGN_TEMPLATE_DEFINITIONS = CAMPAIGN_TEMPLATE_GROUPS.flatMap((group) => group.templates);
const CAMPAIGN_TEMPLATE_KEY_SET = new Set(CAMPAIGN_TEMPLATE_DEFINITIONS.map((template) => template.key));

const isCampaignTemplateKey = (key: string): boolean => key.indexOf('Campaign_') === 0;

const buildDefaultChannel = (definition: CampaignTemplateDefinition): Record<string, JsonValue> => ({
  send: true,
  ab: false,
  message: [{
    name: 'A',
    title: definition.defaultTitle,
    text: definition.defaultText,
  }],
});

const buildDefaultCampaignEntry = (definition: CampaignTemplateDefinition): Record<string, JsonValue> => ({
  type: definition.key,
  send: true,
  [definition.channel]: buildDefaultChannel(definition),
});

const buildBasicEmailPreview = (title: JsonValue, text: JsonValue): string => `
<!doctype html>
<html>
  <head>
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body { margin: 0; background: #eef3ef; font-family: Arial, sans-serif; color: #101814; }
      .card { max-width: 640px; margin: 0 auto; background: #ffffff; }
      .hero { padding: 38px 32px; color: #ffffff; background: linear-gradient(135deg, #101814, #1f6f43); }
      .body { padding: 28px 32px; }
      .cta { display: inline-block; margin-top: 18px; padding: 12px 18px; border-radius: 8px; background: #22a45a; color: #ffffff; text-decoration: none; font-weight: 700; }
      @media (max-width: 600px) { .hero, .body { padding: 24px 18px; } }
    </style>
  </head>
  <body>
    <div class="card">
      <div class="hero"><h1>${String(title || 'Ultrain')}</h1></div>
      <div class="body"><p>${String(text || '')}</p><a class="cta" href="{{deepLink}}">Open Ultrain</a></div>
    </div>
  </body>
</html>`;

const getMessageArray = (channel: JsonValue): Record<string, JsonValue>[] => {
  if (!isRecord(channel) || !Array.isArray(channel.message)) return [];
  return channel.message.filter(isRecord) as Record<string, JsonValue>[];
};

const buildDefaultMessageVariant = (index: number): Record<string, JsonValue> => ({
  name: index < 26 ? String.fromCharCode(65 + index) : `Variant ${index + 1}`,
  title: '',
  text: '',
});

const buildDefaultNotificationChannel = (): Record<string, JsonValue> => ({
  send: true,
  ab: false,
  message: [buildDefaultMessageVariant(0)],
});

const getCampaignMessage = (
  data: JsonValue,
  templateKey: string,
  channelName: CampaignChannel,
): Record<string, JsonValue> => {
  if (!isRecord(data) || !isRecord(data[templateKey])) return {};
  const channel = data[templateKey][channelName];
  const messages = getMessageArray(channel);
  return messages[0] || {};
};

const CampaignTemplatePreview: React.FC<{
  channel: CampaignChannel;
  title: JsonValue;
  text: JsonValue;
  mode: 'desktop' | 'mobile';
  height?: number | string;
  frameless?: boolean;
}> = ({ channel, title, text, mode, height, frameless = false }) => {
  if (channel === 'push') {
    return (
      <div
        className="mx-auto"
        style={{
          width: '100%',
          maxWidth: mode === 'mobile' ? 340 : 420,
          borderRadius: 22,
          background: 'rgba(248, 250, 249, 0.98)',
          padding: '12px 14px 14px',
          boxShadow: '0 14px 38px rgba(17, 24, 20, 0.24)',
          border: '1px solid rgba(255, 255, 255, 0.78)',
        }}
      >
        <div className="d-flex align-items-center mb-2">
          <img
            src="/logo192.png"
            alt=""
            className="mr-2"
            style={{ width: 28, height: 28, borderRadius: 7 }}
          />
          <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
            <strong style={{ color: '#263129', fontSize: 13, lineHeight: 1 }}>Ultrain</strong>
            <span className="mx-2" style={{ color: '#8b948e', fontSize: 12 }}>•</span>
            <span style={{ color: '#747e77', fontSize: 12 }}>now</span>
          </div>
          <span style={{ color: '#7f8982', fontSize: 16, lineHeight: 1 }}>⌄</span>
        </div>
        <div className="d-flex">
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong
              className="d-block mb-1"
              style={{ color: '#111814', fontSize: 15, lineHeight: 1.3 }}
            >
              {String(title || 'Push title')}
            </strong>
            <div
              style={{
                color: '#4d5b52',
                fontSize: 14,
                lineHeight: 1.4,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {String(text || 'Push body')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const rawText = String(text || '');
  const emailDocument = /<html|<body|<table|<div|<p/i.test(rawText)
    ? rawText
    : buildBasicEmailPreview(title, rawText);
  const mobilePreviewStyles = `
    <style>
      html, body { max-width: 100% !important; overflow-x: hidden !important; }
      html, body { scrollbar-width: none !important; }
      ::-webkit-scrollbar { width: 0 !important; height: 0 !important; display: none !important; }
      *, *::before, *::after { box-sizing: border-box; }
      img { max-width: 100% !important; height: auto !important; }
      table { max-width: 100% !important; }
    </style>
  `;
  const srcDoc = mode === 'mobile'
    ? emailDocument.replace(/<head([^>]*)>/i, `<head$1>${mobilePreviewStyles}`)
    : emailDocument;

  return (
    <div
      className="mx-auto"
      style={{
        width: frameless ? '100%' : (mode === 'mobile' ? 300 : '100%'),
        maxWidth: frameless ? 'none' : (mode === 'mobile' ? 300 : 680),
        border: frameless ? 0 : '1px solid #d8e1da',
        borderRadius: frameless ? 0 : 10,
        overflow: 'hidden',
        background: frameless ? 'transparent' : '#eef3ef',
        height,
      }}
    >
      <iframe
        title="Campaign email preview"
        sandbox=""
        srcDoc={srcDoc}
        style={{
          width: '100%',
          height: height || (mode === 'mobile' ? 520 : 420),
          border: 0,
          display: 'block',
          background: frameless ? 'transparent' : '#eef3ef',
        }}
      />
    </div>
  );
};

const getNotificationPreviewOptions = (
  channelName: CampaignChannel,
  channel: Record<string, JsonValue>,
): NotificationPreviewOption[] => {
  const options: NotificationPreviewOption[] = [];

  if (channelName === 'email' && isRecord(channel.content)) {
    Object.entries(channel.content).forEach(([role, localizedByLanguage]) => {
      if (!isRecord(localizedByLanguage)) return;

      Object.entries(localizedByLanguage).forEach(([language, localized]) => {
        if (!isRecord(localized)) return;
        const title = localized.subject;
        const text = localized.html || localized.text;
        if (!String(title || text || '').trim()) return;

        options.push({
          id: `localized-${role}-${language}`,
          label: `${humanizeKey(role)} / ${language.toUpperCase()}`,
          title,
          text,
        });
      });
    });
  }

  getMessageArray(channel).forEach((message, messageIndex) => {
    const variantName = String(message.name || buildDefaultMessageVariant(messageIndex).name);
    if (!String(message.title || message.text || '').trim()) return;

    options.push({
      id: `variant-${messageIndex}`,
      label: `${variantName} ${channelName === 'email' ? 'fallback' : 'variant'}`,
      title: message.title,
      text: message.text,
    });
  });

  return options;
};

const NotificationChannelPreview: React.FC<{
  channelName: CampaignChannel;
  channel: Record<string, JsonValue>;
}> = ({ channelName, channel }) => {
  const [selectedPreviewId, setSelectedPreviewId] = useState('');
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('mobile');
  const previewOptions = getNotificationPreviewOptions(channelName, channel);
  const selectedPreview = previewOptions.find((option) => option.id === selectedPreviewId)
    || previewOptions[0];

  return (
    <div className="border-top mt-3 pt-3">
      <div className="d-flex flex-wrap justify-content-between align-items-end mb-3">
        <div className="mb-2">
          <strong>Live preview</strong>
          <div className="small text-muted">
            {channelName === 'email'
              ? 'Localized content has priority over legacy fallback variants.'
              : 'Preview the notification as it may appear on a device.'}
          </div>
        </div>
        <div className="d-flex flex-wrap align-items-end mb-2">
          {previewOptions.length > 1 ? (
            <Form.Control
              as="select"
              size="sm"
              value={selectedPreview?.id || ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSelectedPreviewId(event.target.value)}
              className="mr-2"
              style={{ width: 190 }}
              aria-label={`${humanizeKey(channelName)} preview content`}
            >
              {previewOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </Form.Control>
          ) : null}
          <Button
            size="sm"
            variant={previewMode === 'desktop' ? 'primary' : 'outline-primary'}
            onClick={() => setPreviewMode('desktop')}
          >
            Desktop
          </Button>
          <Button
            size="sm"
            className="ml-1"
            variant={previewMode === 'mobile' ? 'primary' : 'outline-primary'}
            onClick={() => setPreviewMode('mobile')}
          >
            Mobile
          </Button>
        </div>
      </div>

      {selectedPreview ? (
        <CampaignTemplatePreview
          channel={channelName}
          title={selectedPreview.title}
          text={selectedPreview.text}
          mode={previewMode}
        />
      ) : (
        <Alert variant="secondary" className="mb-0">
          Add {channelName === 'email' ? 'localized content or a fallback message' : 'a message variant'} to preview it.
        </Alert>
      )}
    </div>
  );
};

const NotificationCompactChannelPreview: React.FC<{
  notificationKey: string;
  channelName: CampaignChannel;
  channel: Record<string, JsonValue> | undefined;
  editMode: boolean;
  languages: EmailLanguageOption[];
  onSetValue: (path: string[], value: JsonValue) => void;
  onAddChannel: () => void;
}> = ({
  notificationKey,
  channelName,
  channel,
  editMode,
  languages,
  onSetValue,
  onAddChannel,
}) => {
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('mobile');
  const [roleModeByVariant, setRoleModeByVariant] = useState<Record<number, boolean>>({});
  const [selectedRole, setSelectedRole] = useState('default');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const messages = channel ? getMessageArray(channel) : [];
  const editableMessages = messages.length ? messages : [buildDefaultMessageVariant(0)];
  const safeVariantIndex = Math.min(selectedVariantIndex, editableMessages.length - 1);
  const selectedMessage = editableMessages[safeVariantIndex] || buildDefaultMessageVariant(0);
  const variantCount = editableMessages.length;
  const channelContent = channelName === 'email' && channel && isRecord(channel.content) ? channel.content : {};
  const hasRoleContent = Object.keys(channelContent).length > 0;
  const rolesEnabled = channelName === 'email'
    && (roleModeByVariant[safeVariantIndex] ?? hasRoleContent);
  const content = channelContent;
  const roleContent = isRecord(content[selectedRole]) ? content[selectedRole] : {};
  const localized = isRecord(roleContent[selectedLanguage]) ? roleContent[selectedLanguage] : {};
  const hasLocalization = Object.values(localized).some((value) => typeof value === 'string' && value.length > 0);
  const selectedPreview = rolesEnabled && hasLocalization
    ? {
      title: localized.subject,
      text: localized.html || localized.text,
    }
    : {
      title: selectedMessage.title,
      text: selectedMessage.text,
    };

  const handleSetMessageField = (field: 'name' | 'title' | 'text', value: JsonValue) => {
    if (!channel) return;
    const nextMessages = messages.length ? [...messages] : [buildDefaultMessageVariant(0)];
    while (nextMessages.length <= safeVariantIndex) {
      nextMessages.push(buildDefaultMessageVariant(nextMessages.length));
    }
    nextMessages[safeVariantIndex] = {
      ...buildDefaultMessageVariant(safeVariantIndex),
      ...(nextMessages[safeVariantIndex] || {}),
      [field]: value,
    };
    onSetValue([notificationKey, channelName, 'message'], nextMessages);
  };

  const handleSetLocalizedField = (field: 'subject' | 'preheader' | 'html' | 'text', value: JsonValue) => {
    onSetValue([notificationKey, 'email', 'content', selectedRole, selectedLanguage, field], value);
  };

  const handleAddVariant = () => {
    if (!channel) return;
    const nextMessages = messages.length ? [...messages] : [buildDefaultMessageVariant(0)];
    nextMessages.push(buildDefaultMessageVariant(nextMessages.length));
    onSetValue([notificationKey, channelName, 'message'], nextMessages);
    setSelectedVariantIndex(nextMessages.length - 1);
  };

  const handleToggleRoles = () => {
    if (channelName !== 'email') return;
    const nextRolesEnabled = !rolesEnabled;
    setRoleModeByVariant((current) => ({
      ...current,
      [safeVariantIndex]: nextRolesEnabled,
    }));
    if (!nextRolesEnabled) return;

    setSelectedRole('default');
    setSelectedLanguage('en');
    const defaultContent = isRecord(content.default) && isRecord(content.default.en)
      ? content.default.en
      : {};
    const hasDefaultContent = Object.values(defaultContent).some(
      (value) => typeof value === 'string' && value.length > 0,
    );
    const seedSubject = String(selectedMessage.title || '');
    const seedHtml = String(selectedMessage.text || '');
    if (!hasDefaultContent && String(seedSubject || seedHtml).trim()) {
      onSetValue([notificationKey, 'email', 'content', 'default', 'en'], {
        subject: seedSubject,
        html: seedHtml,
        text: stripHtml(selectedMessage.text),
      });
    }
  };

  const renderVariantTabs = () => (
    <div className="d-flex flex-wrap align-items-center mt-2">
      <div className="btn-group btn-group-sm mr-2 mb-1" role="tablist" aria-label={`${humanizeKey(channelName)} variants`}>
        {editableMessages.map((message, messageIndex) => (
          <Button
            key={`${notificationKey}-${channelName}-variant-tab-${messageIndex}`}
            size="sm"
            variant={safeVariantIndex === messageIndex ? 'primary' : 'outline-primary'}
            onClick={() => setSelectedVariantIndex(messageIndex)}
          >
            {String(message.name || buildDefaultMessageVariant(messageIndex).name)}
          </Button>
        ))}
        <Button
          size="sm"
          variant="outline-primary"
          onClick={handleAddVariant}
          title={`Add ${humanizeKey(channelName)} variant`}
        >
          +
        </Button>
      </div>

      {channelName === 'email' ? (
        <>
          <Form.Check
            id={`${notificationKey}-${channelName}-variant-${safeVariantIndex}-roles`}
            type="switch"
            label="Roles"
            checked={rolesEnabled}
            onChange={handleToggleRoles}
            className="mr-2 mb-1"
          />
          {rolesEnabled ? (
            <>
              <div className="btn-group btn-group-sm mr-2 mb-1" role="tablist" aria-label="Email roles">
                {EMAIL_ROLE_OPTIONS.map((role) => (
                  <Button
                    key={role.value}
                    size="sm"
                    variant={selectedRole === role.value ? 'secondary' : 'outline-secondary'}
                    onClick={() => setSelectedRole(role.value)}
                  >
                    {role.value === 'default' ? 'Coach/default' : role.label}
                  </Button>
                ))}
              </div>
              <Form.Control
                as="select"
                size="sm"
                value={selectedLanguage}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSelectedLanguage(event.target.value)}
                className="mb-1"
                style={{ width: 92 }}
                aria-label="Email language"
              >
                {languages.map((language) => (
                  <option key={language.code} value={language.code}>{language.code.toUpperCase()}</option>
                ))}
              </Form.Control>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );

  const renderEditBody = () => {
    if (!channel) {
      return (
        <Alert variant="light" className="mb-0">
          Add this channel to edit copy and delivery settings.
        </Alert>
      );
    }

    if (channelName === 'email' && rolesEnabled) {
      return (
        <>
          <Row>
            <Col md={8} className="mb-3">
              <Form.Label>Subject</Form.Label>
              <Form.Control
                size="sm"
                value={String(localized.subject || '')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleSetLocalizedField('subject', event.target.value)}
              />
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Preheader</Form.Label>
              <Form.Control
                size="sm"
                value={String(localized.preheader || '')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleSetLocalizedField('preheader', event.target.value)}
              />
            </Col>
          </Row>
          <Form.Group>
            <Form.Label>HTML body</Form.Label>
            <Form.Control
              as="textarea"
              rows={channelName === 'email' ? 8 : 4}
              value={String(localized.html || '')}
              onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleSetLocalizedField('html', event.target.value)}
              style={{
                fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                fontSize: 12,
              }}
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Plain text fallback</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={String(localized.text || '')}
              onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleSetLocalizedField('text', event.target.value)}
            />
          </Form.Group>
        </>
      );
    }

    return (
      <>
        <Row>
          <Col md={4} className="mb-3">
            <Form.Label>Variant</Form.Label>
            <Form.Control
              size="sm"
              value={String(selectedMessage.name || buildDefaultMessageVariant(safeVariantIndex).name)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleSetMessageField('name', event.target.value)}
            />
          </Col>
          <Col md={8} className="mb-3">
            <Form.Label>{channelName === 'email' ? 'Subject' : 'Title'}</Form.Label>
            <Form.Control
              size="sm"
              value={String(selectedMessage.title || '')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleSetMessageField('title', event.target.value)}
            />
          </Col>
        </Row>
        <Form.Group className="mb-0">
          <Form.Label>{channelName === 'email' ? 'Body / HTML' : 'Body'}</Form.Label>
          <Form.Control
            as="textarea"
            rows={channelName === 'email' ? 8 : 4}
            value={String(selectedMessage.text || '')}
            onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleSetMessageField('text', event.target.value)}
            style={{
              fontFamily: channelName === 'email'
                ? 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace'
                : undefined,
              fontSize: channelName === 'email' ? 12 : undefined,
            }}
          />
        </Form.Group>
      </>
    );
  };

  const renderPreviewBody = () => {
    if (!channel) {
      return (
        <Alert variant="light" className="mb-0">
          Add this channel in edit mode to configure copy and delivery settings.
        </Alert>
      );
    }

    if (!String(selectedPreview.title || selectedPreview.text || '').trim()) {
      return (
        <Alert variant="secondary" className="mb-0">
          Add {channelName === 'email' ? 'localized content or a fallback message' : 'a message variant'} to preview it.
        </Alert>
      );
    }

    return (
      <>
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
          <div className="small text-muted mb-1">
            {rolesEnabled && channelName === 'email'
              ? `${EMAIL_ROLE_OPTIONS.find((role) => role.value === selectedRole)?.label || selectedRole} / ${selectedLanguage.toUpperCase()}`
              : `Variant ${String(selectedMessage.name || buildDefaultMessageVariant(safeVariantIndex).name)}`}
          </div>
          <div className="mb-1">
            <Button
              size="sm"
              variant={previewMode === 'desktop' ? 'primary' : 'outline-primary'}
              onClick={() => setPreviewMode('desktop')}
            >
              Desktop
            </Button>
            <Button
              size="sm"
              className="ml-1"
              variant={previewMode === 'mobile' ? 'primary' : 'outline-primary'}
              onClick={() => setPreviewMode('mobile')}
            >
              Mobile
            </Button>
          </div>
        </div>
        <CampaignTemplatePreview
          channel={channelName}
          title={selectedPreview.title}
          text={selectedPreview.text}
          mode={previewMode}
          height={channelName === 'email' ? (previewMode === 'mobile' ? 460 : 360) : undefined}
        />
      </>
    );
  };

  return (
    <div
      className="border rounded h-100"
      style={{
        background: '#ffffff',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
        overflow: 'hidden',
      }}
    >
      <div
        className="d-flex flex-wrap justify-content-between align-items-center px-3 py-2"
        style={{ borderBottom: '1px solid #edf1ee', background: '#f8faf9' }}
      >
        <div className="mr-3 mb-2">
          <div className="d-flex align-items-center">
            <strong className="mr-2">{humanizeKey(channelName)}</strong>
            {channel ? (
              <span className={`badge badge-${channel.send === false ? 'secondary' : 'success'}`}>
                {channel.send === false ? 'Disabled' : 'Enabled'}
              </span>
            ) : (
              <span className="badge badge-light">Not configured</span>
            )}
          </div>
          <div className="small text-muted">
            {channel
              ? `${variantCount || 0} variant(s)${channel.ab ? ', AB on' : ''}`
              : `No ${channelName} channel yet`}
          </div>
          {channel ? renderVariantTabs() : null}
        </div>

        {channel ? (
          <div className="d-flex flex-wrap align-items-center mb-2">
            <Form.Check
              id={`${notificationKey}-${channelName}-selected-send`}
              type="switch"
              label="Enabled"
              checked={channel.send !== false}
              onChange={() => onSetValue([notificationKey, channelName, 'send'], channel.send === false)}
              className="mr-3"
            />
            <Form.Check
              id={`${notificationKey}-${channelName}-selected-ab`}
              type="switch"
              label="AB"
              checked={Boolean(channel.ab)}
              onChange={() => onSetValue([notificationKey, channelName, 'ab'], !Boolean(channel.ab))}
            />
          </div>
        ) : (
          <Button size="sm" variant="outline-primary" className="mb-2" onClick={onAddChannel}>
            Add {humanizeKey(channelName)}
          </Button>
        )}
      </div>

      <div className="p-3">
        {editMode ? renderEditBody() : renderPreviewBody()}
      </div>
    </div>
  );
};

const NotificationListHoverPreview: React.FC<{
  notificationKey: string;
  entry: Record<string, JsonValue>;
}> = ({ notificationKey, entry }) => {
  const push = isRecord(entry.push) ? entry.push : undefined;
  const email = isRecord(entry.email) ? entry.email : undefined;
  const pushPreview = push ? getNotificationPreviewOptions('push', push)[0] : undefined;
  const emailPreview = email ? getNotificationPreviewOptions('email', email)[0] : undefined;

  if (!pushPreview && !emailPreview) return null;

  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 1060,
        top: 72,
        right: 16,
        bottom: emailPreview ? 16 : undefined,
        width: 340,
        maxHeight: 'calc(100vh - 88px)',
        overflow: 'hidden',
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
      aria-label={`${humanizeKey(notificationKey)} mobile preview`}
    >
      {pushPreview ? (
        <div style={{ flex: '0 0 auto' }}>
          <CampaignTemplatePreview
            channel="push"
            title={pushPreview.title}
            text={pushPreview.text}
            mode="mobile"
          />
        </div>
      ) : null}

      {emailPreview ? (
        <div
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ flex: '1 1 auto', minHeight: 0 }}>
            <CampaignTemplatePreview
              channel="email"
              title={emailPreview.title}
              text={emailPreview.text}
              mode="mobile"
              height="100%"
              frameless
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

const LocalizedEmailContentEditor: React.FC<LocalizedEmailEditorProps> = ({
  notificationKey,
  channel,
  languages,
  onSetValue,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState('default');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

  const content = isRecord(channel.content) ? channel.content : {};
  const roleContent = isRecord(content[selectedRole]) ? content[selectedRole] : {};
  const localized = isRecord(roleContent[selectedLanguage]) ? roleContent[selectedLanguage] : {};
  const englishSource = isRecord(roleContent.en) ? roleContent.en : undefined;
  const requiredVariables = Array.isArray(channel.requiredVariables)
    ? channel.requiredVariables.filter((variable) => typeof variable === 'string').map(String)
    : [];
  const variableOptions = Array.from(new Set([...requiredVariables, ...COMMON_EMAIL_VARIABLES]));
  const localizationPath = [notificationKey, 'email', 'content', selectedRole, selectedLanguage];
  const hasLocalization = Object.values(localized).some((value) => typeof value === 'string' && value.length > 0);

  const handleFieldChange = (field: 'subject' | 'preheader' | 'html' | 'text', value: string) => {
    onSetValue([...localizationPath, field], value);
  };

  const handleCopyFromEnglish = () => {
    if (!englishSource || selectedLanguage === 'en') return;
    onSetValue(localizationPath, cloneJson(englishSource));
  };

  const handleInsertVariable = (variable: string) => {
    const html = String(localized.html || '');
    handleFieldChange('html', `${html}${html ? ' ' : ''}{{${variable}}}`);
  };

  return (
    <details
      className="border rounded mt-3"
      onToggle={(event) => setIsOpen(event.currentTarget.open)}
    >
      <summary className="p-3" style={{ cursor: 'pointer' }}>
        <strong>Localized content by role and language</strong>
        <span className="small text-muted ml-2">
          {isRecord(channel.content) ? `${Object.keys(channel.content).length} role set(s)` : 'Legacy fallback active'}
        </span>
      </summary>

      {isOpen ? (
        <div className="border-top p-3">
          <Alert variant="info">
            Resolution order: selected role/language → default role/language → selected role/English →
            default/English → current Mailgun or inline template.
          </Alert>

          <Row>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Recipient role</Form.Label>
                <Form.Control
                  as="select"
                  value={selectedRole}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSelectedRole(event.target.value)}
                >
                  {EMAIL_ROLE_OPTIONS.map((role) => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Language</Form.Label>
                <Form.Control
                  as="select"
                  value={selectedLanguage}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSelectedLanguage(event.target.value)}
                >
                  {languages.map((language) => (
                    <option key={language.code} value={language.code}>
                      {language.label} ({language.code})
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                block
                variant="outline-secondary"
                className="mb-3"
                disabled={selectedLanguage === 'en' || !englishSource}
                onClick={handleCopyFromEnglish}
              >
                Copy EN
              </Button>
            </Col>
          </Row>

          <Form.Group>
            <Form.Label>Required variables</Form.Label>
            <Form.Control
              value={requiredVariables.join(', ')}
              placeholder="firstName, deepLink"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const variables = event.target.value
                  .split(',')
                  .map((variable) => variable.trim())
                  .filter(Boolean);
                onSetValue([notificationKey, 'email', 'requiredVariables'], variables);
              }}
            />
            <Form.Text className="text-muted">
              Backend refuses to queue this localized email if any required value is missing.
            </Form.Text>
          </Form.Group>

          <Row>
            <Col xl={7}>
              <Form.Group>
                <Form.Label>Subject</Form.Label>
                <Form.Control
                  value={String(localized.subject || '')}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange('subject', event.target.value)}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Preheader</Form.Label>
                <Form.Control
                  value={String(localized.preheader || '')}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange('preheader', event.target.value)}
                />
              </Form.Group>

              <Form.Group>
                <div className="d-flex flex-wrap justify-content-between align-items-center">
                  <Form.Label>Responsive HTML body</Form.Label>
                  <div className="mb-2">
                    {variableOptions.map((variable) => (
                      <Button
                        key={variable}
                        size="sm"
                        variant="outline-secondary"
                        className="ml-1 mb-1"
                        onClick={() => handleInsertVariable(variable)}
                      >
                        {`{{${variable}}}`}
                      </Button>
                    ))}
                  </div>
                </div>
                <Form.Control
                  as="textarea"
                  rows={12}
                  value={String(localized.html || '')}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                    handleFieldChange('html', event.target.value)}
                  style={{
                    fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                    fontSize: 12,
                  }}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Plain text fallback</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={String(localized.text || '')}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                    handleFieldChange('text', event.target.value)}
                />
              </Form.Group>
            </Col>

            <Col xl={5}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Preview</strong>
                <div>
                  <Button
                    size="sm"
                    variant={previewMode === 'desktop' ? 'primary' : 'outline-primary'}
                    onClick={() => setPreviewMode('desktop')}
                  >
                    Desktop
                  </Button>
                  <Button
                    size="sm"
                    className="ml-1"
                    variant={previewMode === 'mobile' ? 'primary' : 'outline-primary'}
                    onClick={() => setPreviewMode('mobile')}
                  >
                    Mobile
                  </Button>
                </div>
              </div>
              {!hasLocalization ? (
                <Alert variant="secondary">
                  No content for this role/language. Backend will use the documented fallback chain.
                </Alert>
              ) : (
                <CampaignTemplatePreview
                  channel="email"
                  title={localized.subject}
                  text={localized.html || localized.text}
                  mode={previewMode}
                />
              )}
            </Col>
          </Row>
        </div>
      ) : null}
    </details>
  );
};

const NotificationPolicyEditor: React.FC<StructuredEditorProps> = ({ data, onSetValue }) => {
  if (!isRecord(data)) return null;

  const policyKeys = Object.keys(data).filter((key) => isRecord(data[key]));
  if (policyKeys.length === 0) return null;

  return (
    <Card className="mb-4" style={adminCardStyle}>
      <Card.Header style={adminCardHeaderStyle}>
        <strong>Notification Policy</strong>
        <div className="small text-muted">
          Control notification recipients and delivery rules from app_config.notification_policy.
        </div>
      </Card.Header>
      <Card.Body>
        {policyKeys.map((policyKey) => {
          const policy = data[policyKey] as Record<string, JsonValue>;
          const channels = isRecord(policy.channels) ? policy.channels : {};
          const rules = isRecord(policy.rules) ? policy.rules : {};
          const ruleTargets = collectRuleTargets(rules);
          const minorHandling = isRecord(policy.minorHandling) ? policy.minorHandling : undefined;
          const extraSettingKeys = Object.keys(policy)
            .filter((fieldKey) => POLICY_CORE_FIELDS.indexOf(fieldKey) === -1)
            .filter((fieldKey) => isEditablePolicySetting(policy[fieldKey]));

          return (
            <div className="border rounded p-3 mb-3" key={policyKey}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <strong>{humanizeKey(policyKey)}</strong>
                  <div className="small text-muted"><code>{policyKey}</code></div>
                  {POLICY_DESCRIPTIONS[policyKey] && (
                    <div className="small text-muted">{POLICY_DESCRIPTIONS[policyKey]}</div>
                  )}
                </div>
                <Form.Check
                  id={`${policyKey}-enabled`}
                  type="switch"
                  label="Enabled"
                  checked={Boolean(policy.enabled)}
                  onChange={() => onSetValue([policyKey, 'enabled'], !Boolean(policy.enabled))}
                />
              </div>

              <Row>
                <Col md={4}>
                  <Form.Check
                    id={`${policyKey}-notify-actor`}
                    type="switch"
                    label="Notify actor"
                    checked={Boolean(policy.notifyActor)}
                    onChange={() => onSetValue([policyKey, 'notifyActor'], !Boolean(policy.notifyActor))}
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    id={`${policyKey}-push`}
                    type="switch"
                    label="Push"
                    checked={Boolean(channels.push)}
                    onChange={() => onSetValue([policyKey, 'channels', 'push'], !Boolean(channels.push))}
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    id={`${policyKey}-email`}
                    type="switch"
                    label="Email"
                    checked={Boolean(channels.email)}
                    onChange={() => onSetValue([policyKey, 'channels', 'email'], !Boolean(channels.email))}
                  />
                </Col>
              </Row>

              {extraSettingKeys.length > 0 && (
                <div className="border-top mt-3 pt-3">
                  <strong className="d-block mb-2">Additional Settings</strong>
                  <Row>
                    {extraSettingKeys.map((settingKey) => {
                      const value = policy[settingKey];

                      if (typeof value === 'boolean') {
                        return (
                          <Col md={4} key={settingKey} className="mb-2">
                            <Form.Check
                              id={`${policyKey}-setting-${settingKey}`}
                              type="switch"
                              label={humanizeKey(settingKey)}
                              checked={value}
                              onChange={() => onSetValue([policyKey, settingKey], !value)}
                            />
                          </Col>
                        );
                      }

                      return (
                        <Col md={6} key={settingKey} className="mb-2">
                          <Form.Label>{humanizeKey(settingKey)}</Form.Label>
                          <Form.Control
                            size="sm"
                            type={typeof value === 'number' ? 'number' : 'text'}
                            value={value === undefined || value === null ? '' : String(value)}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                              onSetValue(
                                [policyKey, settingKey],
                                typeof value === 'number' ? Number(event.target.value) : event.target.value,
                              )}
                          />
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              )}

              {Object.keys(rules).length > 0 && (
                <Table responsive bordered size="sm" className="mt-3 mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '28%' }}>Rule</th>
                      {ruleTargets.map((targetKey) => (
                        <th key={targetKey}>{humanizeKey(targetKey)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(rules).map((ruleKey) => {
                      const rule = isRecord(rules[ruleKey]) ? rules[ruleKey] : {};
                      return (
                        <tr key={ruleKey}>
                          <td><code>{ruleKey}</code></td>
                          {ruleTargets.map((targetKey) => (
                            <td key={targetKey}>
                              <Form.Check
                                id={`${policyKey}-${ruleKey}-${targetKey}`}
                                type="switch"
                                checked={Boolean(rule[targetKey])}
                                label=""
                                onChange={() => onSetValue(
                                  [policyKey, 'rules', ruleKey, targetKey],
                                  !Boolean(rule[targetKey]),
                                )}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}

              {minorHandling && (
                <div className="border-top mt-3 pt-3">
                  <strong className="d-block mb-2">Minor Handling</strong>
                  <Row>
                    {Object.keys(minorHandling).map((minorKey) => {
                      const value = minorHandling[minorKey];
                      if (typeof value === 'boolean') {
                        return (
                          <Col md={4} key={minorKey} className="mb-2">
                            <Form.Check
                              id={`${policyKey}-minor-${minorKey}`}
                              type="switch"
                              label={humanizeKey(minorKey)}
                              checked={value}
                              onChange={() => onSetValue([policyKey, 'minorHandling', minorKey], !value)}
                            />
                          </Col>
                        );
                      }

                      return (
                        <Col md={6} key={minorKey} className="mb-2">
                          <Form.Label>{humanizeKey(minorKey)}</Form.Label>
                          <Form.Control
                            size="sm"
                            value={value === undefined || value === null ? '' : String(value)}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                              onSetValue([policyKey, 'minorHandling', minorKey], event.target.value)}
                          />
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              )}
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
};

const CampaignTemplateEditor: React.FC<StructuredEditorProps & { languages: EmailLanguageOption[] }> = ({
  data,
  onSetValue,
  onReplaceData,
  languages,
}) => {
  const [selectedGroupKey, setSelectedGroupKey] = useState(CAMPAIGN_TEMPLATE_GROUPS[0].key);
  const [filter, setFilter] = useState('');
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

  if (!isRecord(data)) return null;

  const existingCampaignKeys = Object.keys(data).filter(isCampaignTemplateKey);
  const isUnknownGroup = selectedGroupKey === 'unknown';
  const selectedGroup = CAMPAIGN_TEMPLATE_GROUPS.find((group) => group.key === selectedGroupKey)
    || CAMPAIGN_TEMPLATE_GROUPS[0];
  const normalizedFilter = filter.trim().toLowerCase();
  const unknownDefinitions: CampaignTemplateDefinition[] = existingCampaignKeys
    .filter((key) => !CAMPAIGN_TEMPLATE_KEY_SET.has(key))
    .map((key) => ({
      key,
      channel: isRecord(data[key]) && isRecord((data[key] as Record<string, JsonValue>).email) ? 'email' : 'push',
      title: humanizeKey(key),
      description: 'Campaign template not listed in the current registry.',
      defaultTitle: humanizeKey(key),
      defaultText: '',
    }));

  const visibleDefinitions = (isUnknownGroup
    ? unknownDefinitions
    : selectedGroup.templates
  ).filter((definition) => {
    if (!normalizedFilter) return true;
    return [definition.key, definition.title, definition.description]
      .join(' ')
      .toLowerCase()
      .indexOf(normalizedFilter) !== -1;
  });

  const missingCount = CAMPAIGN_TEMPLATE_DEFINITIONS.reduce((total, definition) => {
    const entry = isRecord(data[definition.key]) ? data[definition.key] : undefined;
    return total + (entry && isRecord(entry[definition.channel]) ? 0 : 1);
  }, 0);

  const handleAddTemplate = (definition: CampaignTemplateDefinition) => {
    const currentEntry = isRecord(data[definition.key]) ? data[definition.key] : {};
    onSetValue([definition.key], {
      type: definition.key,
      send: true,
      ...currentEntry,
      [definition.channel]: {
        ...buildDefaultChannel(definition),
        ...(isRecord(currentEntry[definition.channel]) ? currentEntry[definition.channel] : {}),
      },
    });
  };

  const handleAddMissingTemplates = () => {
    if (!onReplaceData) return;
    const nextData = cloneJson(data);
    CAMPAIGN_TEMPLATE_DEFINITIONS.forEach((definition) => {
      const currentEntry = isRecord(nextData[definition.key]) ? nextData[definition.key] : undefined;
      if (!currentEntry) {
        nextData[definition.key] = buildDefaultCampaignEntry(definition);
        return;
      }

      if (!isRecord(currentEntry[definition.channel])) {
        currentEntry[definition.channel] = buildDefaultChannel(definition);
      }
      if (typeof currentEntry.type === 'undefined') currentEntry.type = definition.key;
      if (typeof currentEntry.send === 'undefined') currentEntry.send = true;
    });
    onReplaceData(nextData);
  };

  const handleSetMessageField = (
    templateKey: string,
    channelName: CampaignChannel,
    field: 'name' | 'title' | 'text',
    value: JsonValue,
  ) => {
    const entry = isRecord(data[templateKey]) ? data[templateKey] : {};
    const channel = isRecord(entry[channelName]) ? entry[channelName] : {};
    const messages = getMessageArray(channel);
    const firstMessage = {
      name: 'A',
      ...(messages[0] || {}),
      [field]: value,
    };
    const nextMessages = messages.length ? [...messages] : [firstMessage];
    nextMessages[0] = firstMessage;
    onSetValue([templateKey, channelName, 'message'], nextMessages);
  };

  return (
    <Card className="mb-4" style={adminCardStyle}>
      <Card.Header style={adminCardHeaderStyle}>
        <div className="d-flex flex-wrap justify-content-between align-items-start">
          <div className="mb-2">
            <strong>Campaign Templates</strong>
            <div className="small text-muted">
              Editable lifecycle campaign copy from app_config.notification.
            </div>
          </div>
          <div className="d-flex flex-wrap align-items-center">
            <Form.Control
              size="sm"
              placeholder="Filter campaign"
              value={filter}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFilter(event.target.value)}
              className="mr-2 mb-2"
              style={{ width: 220 }}
            />
            <Button
              size="sm"
              variant={previewMode === 'desktop' ? 'primary' : 'outline-primary'}
              onClick={() => setPreviewMode('desktop')}
              className="mb-2"
            >
              Desktop
            </Button>
            <Button
              size="sm"
              variant={previewMode === 'mobile' ? 'primary' : 'outline-primary'}
              onClick={() => setPreviewMode('mobile')}
              className="ml-1 mb-2"
            >
              Mobile
            </Button>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="d-flex flex-wrap mb-3">
          {CAMPAIGN_TEMPLATE_GROUPS.map((group) => (
            <Button
              key={group.key}
              size="sm"
              variant={selectedGroupKey === group.key ? 'primary' : 'outline-secondary'}
              className="mr-2 mb-2"
              onClick={() => setSelectedGroupKey(group.key)}
            >
              {group.title}
            </Button>
          ))}
          {unknownDefinitions.length > 0 && (
            <Button
              size="sm"
              variant={selectedGroupKey === 'unknown' ? 'primary' : 'outline-secondary'}
              className="mr-2 mb-2"
              onClick={() => setSelectedGroupKey('unknown')}
            >
              Other Campaign Keys
            </Button>
          )}
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div>
            <strong>{isUnknownGroup ? 'Other Campaign Keys' : selectedGroup.title}</strong>
            <div className="small text-muted">
              {isUnknownGroup
                ? 'Existing Campaign_* keys that are not part of the known Darius plan.'
                : selectedGroup.description}
            </div>
          </div>
          {missingCount > 0 && (
            <Button size="sm" variant="outline-success" onClick={handleAddMissingTemplates}>
              Add missing campaign templates ({missingCount})
            </Button>
          )}
        </div>

        {visibleDefinitions.length === 0 && (
          <Alert variant="info" className="mb-0">No campaign templates match this filter.</Alert>
        )}

        {visibleDefinitions.map((definition) => {
          const entry = isRecord(data[definition.key]) ? data[definition.key] : undefined;
          const channel = entry && isRecord(entry[definition.channel]) ? entry[definition.channel] : undefined;
          const message = getCampaignMessage(data, definition.key, definition.channel);
          const title = message.title || definition.defaultTitle;
          const text = message.text || definition.defaultText;
          const plainPreview = definition.channel === 'email'
            ? truncate(stripHtml(text), 160)
            : truncate(text, 160);

          return (
            <div className="border rounded p-3 mb-3" key={definition.key}>
              <div className="d-flex flex-wrap justify-content-between align-items-start mb-3">
                <div className="mb-2">
                  <div className="d-flex align-items-center">
                    <strong className="mr-2">{definition.title}</strong>
                    <span className={`badge badge-${definition.channel === 'email' ? 'info' : 'success'}`}>
                      {definition.channel.toUpperCase()}
                    </span>
                  </div>
                  <div className="small text-muted">{definition.description}</div>
                  <div className="small"><code>{definition.key}</code></div>
                </div>
                <div className="d-flex flex-wrap align-items-center">
                  {entry && (
                    <Form.Check
                      id={`${definition.key}-root-send`}
                      type="switch"
                      label="Notification enabled"
                      checked={entry.send !== false}
                      onChange={() => onSetValue([definition.key, 'send'], entry.send === false)}
                      className="mr-3 mb-2"
                    />
                  )}
                  {channel && (
                    <Form.Check
                      id={`${definition.key}-${definition.channel}-send`}
                      type="switch"
                      label={`${humanizeKey(definition.channel)} enabled`}
                      checked={channel.send !== false}
                      onChange={() => onSetValue(
                        [definition.key, definition.channel, 'send'],
                        channel.send === false,
                      )}
                      className="mb-2"
                    />
                  )}
                </div>
              </div>

              {!channel && (
                <Alert variant="warning" className="mb-0">
                  This campaign has no {definition.channel} template in the current JSON.{' '}
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => handleAddTemplate(definition)}
                  >
                    Add template
                  </Button>
                </Alert>
              )}

              {channel && (
                <Row>
                  <Col xl={7} className="mb-3">
                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Label>Variant name</Form.Label>
                        <Form.Control
                          size="sm"
                          value={String(message.name || 'A')}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleSetMessageField(definition.key, definition.channel, 'name', event.target.value)}
                        />
                      </Col>
                      <Col md={6} className="mb-3">
                        <Form.Label>AB testing</Form.Label>
                        <Form.Check
                          id={`${definition.key}-${definition.channel}-ab`}
                          type="switch"
                          label="Use random message variants"
                          checked={Boolean(channel.ab)}
                          onChange={() => onSetValue(
                            [definition.key, definition.channel, 'ab'],
                            !Boolean(channel.ab),
                          )}
                        />
                      </Col>
                    </Row>

                    {definition.channel === 'email' && (
                      <>
                        <Form.Group>
                          <Form.Label>Mailgun template override</Form.Label>
                          <Form.Control
                            size="sm"
                            value={String(channel.template || '')}
                            placeholder="Leave empty to use HTML body from app_config"
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                              onSetValue([definition.key, definition.channel, 'template'], event.target.value)}
                          />
                          <Form.Text className="text-muted">
                            Legacy fallback. Localized role/language content below has priority when configured.
                          </Form.Text>
                        </Form.Group>
                        <LocalizedEmailContentEditor
                          notificationKey={definition.key}
                          channel={channel}
                          languages={languages}
                          onSetValue={onSetValue}
                        />
                      </>
                    )}

                    <Form.Group>
                      <Form.Label>{definition.channel === 'email' ? 'Email subject' : 'Push title'}</Form.Label>
                      <Form.Control
                        value={String(title || '')}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          handleSetMessageField(definition.key, definition.channel, 'title', event.target.value)}
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>{definition.channel === 'email' ? 'Responsive HTML body' : 'Push body'}</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={definition.channel === 'email' ? 12 : 4}
                        value={String(text || '')}
                        onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          handleSetMessageField(definition.key, definition.channel, 'text', event.target.value)}
                        style={{
                          fontFamily: definition.channel === 'email'
                            ? 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace'
                            : undefined,
                          fontSize: definition.channel === 'email' ? 12 : undefined,
                        }}
                      />
                      {plainPreview && (
                        <Form.Text className="text-muted">
                          Preview text: {plainPreview}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col xl={5} className="mb-3">
                    <CampaignTemplatePreview
                      channel={definition.channel}
                      title={title}
                      text={text}
                      mode={previewMode}
                    />
                  </Col>
                </Row>
              )}
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
};

const NotificationToggleEditor: React.FC<StructuredEditorProps & {
  excludeCampaignKeys?: boolean;
  languages: EmailLanguageOption[];
  singleItemMode?: boolean;
}> = ({
  data,
  onSetValue,
  excludeCampaignKeys,
  languages,
  singleItemMode = false,
}) => {
  const [filter, setFilter] = useState('');

  if (!isRecord(data)) return null;

  const normalizedFilter = filter.trim().toLowerCase();
  const notificationKeys = Object.keys(data)
    .filter((key) => {
      const item = data[key];
      return isRecord(item) && (isRecord(item.push) || isRecord(item.email));
    })
    .filter((key) => !excludeCampaignKeys || !isCampaignTemplateKey(key))
    .filter((key) => normalizedFilter.length === 0 || key.toLowerCase().indexOf(normalizedFilter) !== -1);

  if (notificationKeys.length === 0 && normalizedFilter.length === 0) return null;

  const handleSetChannelMessageField = (
    notificationKey: string,
    channelName: CampaignChannel,
    messageIndex: number,
    field: 'name' | 'title' | 'text',
    value: JsonValue,
  ) => {
    const item = isRecord(data[notificationKey]) ? data[notificationKey] : {};
    const channel = isRecord(item[channelName]) ? item[channelName] : {};
    const messages = getMessageArray(channel);
    const nextMessages = messages.length ? [...messages] : [buildDefaultMessageVariant(0)];
    while (nextMessages.length <= messageIndex) {
      nextMessages.push(buildDefaultMessageVariant(nextMessages.length));
    }
    nextMessages[messageIndex] = {
      ...buildDefaultMessageVariant(messageIndex),
      ...(nextMessages[messageIndex] || {}),
      [field]: value,
    };
    onSetValue([notificationKey, channelName, 'message'], nextMessages);
  };

  const handleAddMessageVariant = (notificationKey: string, channelName: CampaignChannel) => {
    const item = isRecord(data[notificationKey]) ? data[notificationKey] : {};
    const channel = isRecord(item[channelName]) ? item[channelName] : {};
    const messages = getMessageArray(channel);
    const nextMessages = messages.length ? [...messages] : [buildDefaultMessageVariant(0)];
    nextMessages.push(buildDefaultMessageVariant(nextMessages.length));
    onSetValue([notificationKey, channelName, 'message'], nextMessages);
  };

  const handleAddChannel = (notificationKey: string, channelName: CampaignChannel) => {
    onSetValue([notificationKey, channelName], buildDefaultNotificationChannel());
  };

  const renderChannelEditor = (
    notificationKey: string,
    channelName: CampaignChannel,
    channel: Record<string, JsonValue> | undefined,
  ) => {
    if (!channel) {
      return (
        <div className="border rounded p-3 h-100">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <strong>{humanizeKey(channelName)}</strong>
              <div className="small text-muted">No {channelName} channel configured.</div>
            </div>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => handleAddChannel(notificationKey, channelName)}
            >
              Add {humanizeKey(channelName)}
            </Button>
          </div>
        </div>
      );
    }

    const messages = getMessageArray(channel);
    const editableMessages = messages.length ? messages : [buildDefaultMessageVariant(0)];

    return (
      <div className="border rounded p-3 h-100">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div>
            <strong>{humanizeKey(channelName)}</strong>
            <div className="small text-muted">{editableMessages.length} message variant(s)</div>
          </div>
          <div className="d-flex flex-wrap">
            <Form.Check
              id={`${notificationKey}-${channelName}-content-send`}
              type="switch"
              label={`${humanizeKey(channelName)} enabled`}
              checked={channel.send !== false}
              onChange={() => onSetValue([notificationKey, channelName, 'send'], channel.send === false)}
              className="mr-3"
            />
            <Form.Check
              id={`${notificationKey}-${channelName}-content-ab`}
              type="switch"
              label="AB"
              checked={Boolean(channel.ab)}
              onChange={() => onSetValue([notificationKey, channelName, 'ab'], !Boolean(channel.ab))}
            />
            <Button
              size="sm"
              variant="outline-secondary"
              className="ml-3"
              onClick={() => handleAddMessageVariant(notificationKey, channelName)}
            >
              Add Variant
            </Button>
          </div>
        </div>

        {channelName === 'email' && (
          <>
            <Form.Group>
              <Form.Label>Mailgun template</Form.Label>
              <Form.Control
                size="sm"
                value={String(channel.template || '')}
                placeholder="Optional Mailgun template name"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onSetValue([notificationKey, channelName, 'template'], event.target.value)}
              />
              <Form.Text className="text-muted">
                Legacy fallback. Localized role/language content below has priority when configured.
              </Form.Text>
            </Form.Group>
            <LocalizedEmailContentEditor
              notificationKey={notificationKey}
              channel={channel}
              languages={languages}
              onSetValue={onSetValue}
            />
          </>
        )}

        {editableMessages.map((message, messageIndex) => (
          <div
            className={messageIndex === editableMessages.length - 1 ? '' : 'border-bottom pb-3 mb-3'}
            key={`${notificationKey}-${channelName}-${messageIndex}`}
          >
            <Row>
              <Col md={4} className="mb-3">
                <Form.Label>Variant</Form.Label>
                <Form.Control
                  size="sm"
                  value={String(message.name || buildDefaultMessageVariant(messageIndex).name)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleSetChannelMessageField(
                      notificationKey,
                      channelName,
                      messageIndex,
                      'name',
                      event.target.value,
                    )}
                />
              </Col>
              <Col md={8} className="mb-3">
                <Form.Label>{channelName === 'email' ? 'Subject' : 'Title'}</Form.Label>
                <Form.Control
                  size="sm"
                  value={String(message.title || '')}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleSetChannelMessageField(
                      notificationKey,
                      channelName,
                      messageIndex,
                      'title',
                      event.target.value,
                    )}
                />
              </Col>
            </Row>

            <Form.Group className="mb-0">
              <Form.Label>{channelName === 'email' ? 'Body / HTML' : 'Body'}</Form.Label>
              <Form.Control
                as="textarea"
                rows={channelName === 'email' ? 6 : 4}
                value={String(message.text || '')}
                onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleSetChannelMessageField(
                    notificationKey,
                    channelName,
                    messageIndex,
                    'text',
                    event.target.value,
                  )}
                style={{
                  fontFamily: channelName === 'email'
                    ? 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace'
                    : undefined,
                  fontSize: channelName === 'email' ? 12 : undefined,
                }}
              />
            </Form.Group>
          </div>
        ))}

        {!singleItemMode ? (
          <NotificationChannelPreview
            channelName={channelName}
            channel={channel}
          />
        ) : null}
      </div>
    );
  };

  return (
    <Card className="mb-4" style={adminCardStyle}>
      {!singleItemMode ? (
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center" style={adminCardHeaderStyle}>
          <div>
            <strong>General Notification Texts</strong>
            <div className="small text-muted">
              Edit push and email copy for non-campaign notification config entries.
            </div>
          </div>
          <Form.Control
            size="sm"
            placeholder="Filter notification"
            value={filter}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFilter(event.target.value)}
            style={{ maxWidth: 260 }}
          />
        </Card.Header>
      ) : null}
      <Card.Body className={singleItemMode ? 'p-0' : undefined}>
        {notificationKeys.length === 0 && (
          <Alert variant="info" className="mb-0">No notification entries match this filter.</Alert>
        )}

        {notificationKeys.map((notificationKey) => {
          const item = data[notificationKey] as Record<string, JsonValue>;
          const push = isRecord(item.push) ? item.push : undefined;
          const email = isRecord(item.email) ? item.email : undefined;

          return (
            <div
              className={`border rounded p-3 ${singleItemMode ? 'mb-0' : 'mb-3'}`}
              key={notificationKey}
            >
              <div className="d-flex flex-wrap justify-content-between align-items-start mb-3">
                <div className="mb-2">
                  <strong>{humanizeKey(notificationKey)}</strong>
                  <div className="small"><code>{notificationKey}</code></div>
                  {item.type && <div className="small text-muted">Type: {String(item.type)}</div>}
                  {item.interval && <div className="small text-muted">Interval: {String(item.interval)}</div>}
                </div>
                <Form.Check
                  id={`${notificationKey}-root-content-send`}
                  type="switch"
                  label="Notification enabled"
                  checked={item.send !== false}
                  onChange={() => onSetValue([notificationKey, 'send'], item.send === false)}
                />
              </div>

              {(item.title || item.text) && (
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Label>Root title</Form.Label>
                    <Form.Control
                      size="sm"
                      value={String(item.title || '')}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        onSetValue([notificationKey, 'title'], event.target.value)}
                    />
                  </Col>
                  <Col md={8}>
                    <Form.Label>Root text</Form.Label>
                    <Form.Control
                      size="sm"
                      value={String(item.text || '')}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        onSetValue([notificationKey, 'text'], event.target.value)}
                    />
                  </Col>
                </Row>
              )}

              <Row>
                <Col xl={6} className="mb-3">
                  {renderChannelEditor(notificationKey, 'push', push)}
                </Col>
                <Col xl={6} className="mb-3">
                  {renderChannelEditor(notificationKey, 'email', email)}
                </Col>
              </Row>
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
};

const getEmailContentSource = (email: Record<string, JsonValue> | undefined): string => {
  if (!email) return 'Not configured';
  if (isRecord(email.content) && Object.keys(email.content).length > 0) return 'Localized';
  if (String(email.template || '').trim()) return 'Mailgun';
  if (getMessageArray(email).some((message) => String(message.text || '').trim())) return 'Inline';
  return 'Empty';
};

const countEmailLocalizations = (email: Record<string, JsonValue> | undefined): number => {
  if (!email || !isRecord(email.content)) return 0;
  return Object.values(email.content).reduce((total, localizedByLanguage) => {
    if (!isRecord(localizedByLanguage)) return total;
    return total + Object.values(localizedByLanguage).filter((localized) => {
      if (!isRecord(localized)) return false;
      return Boolean(String(localized.subject || localized.html || localized.text || '').trim());
    }).length;
  }, 0);
};

const NotificationTemplateManager: React.FC<StructuredEditorProps & {
  languages: EmailLanguageOption[];
  selectedNotificationKey?: string;
  onSelectNotification: (notificationKey?: string) => void;
}> = ({
  data,
  onSetValue,
  languages,
  selectedNotificationKey,
  onSelectNotification,
}) => {
  const [filter, setFilter] = useState('');
  const [streamFilter, setStreamFilter] = useState<'all' | 'campaign' | 'general'>('all');
  const [hoveredNotificationKey, setHoveredNotificationKey] = useState<string | undefined>();
  const [selectedEditMode, setSelectedEditMode] = useState(false);

  useEffect(() => {
    setSelectedEditMode(false);
  }, [selectedNotificationKey]);

  if (!isRecord(data)) return null;

  const notificationKeys = Object.keys(data)
    .filter((key) => {
      const entry = data[key];
      return isRecord(entry) && (isRecord(entry.push) || isRecord(entry.email));
    })
    .sort((first, second) => first.localeCompare(second));
  const normalizedFilter = filter.trim().toLowerCase();
  const visibleKeys = notificationKeys.filter((key) => {
    const isCampaign = isCampaignTemplateKey(key);
    if (streamFilter === 'campaign' && !isCampaign) return false;
    if (streamFilter === 'general' && isCampaign) return false;
    return !normalizedFilter
      || humanizeKey(key).toLowerCase().includes(normalizedFilter)
      || key.toLowerCase().includes(normalizedFilter);
  });
  const hoveredEntry = hoveredNotificationKey && isRecord(data[hoveredNotificationKey])
    ? data[hoveredNotificationKey] as Record<string, JsonValue>
    : undefined;

  if (selectedNotificationKey) {
    const selectedEntry = data[selectedNotificationKey];
    if (!isRecord(selectedEntry)) {
      return (
        <Alert variant="warning">
          Selected template no longer exists.{' '}
          <Button size="sm" variant="outline-secondary" onClick={() => onSelectNotification(undefined)}>
            Back to templates
          </Button>
        </Alert>
      );
    }

    const email = isRecord(selectedEntry.email) ? selectedEntry.email : undefined;
    const push = isRecord(selectedEntry.push) ? selectedEntry.push : undefined;
    const isCampaignTemplate = isCampaignTemplateKey(selectedNotificationKey);
    const rootCopyPreview = [selectedEntry.title, selectedEntry.text]
      .filter((value) => String(value || '').trim())
      .map((value) => truncate(value, 120))
      .join(' - ');
    const handleAddSelectedChannel = (channelName: CampaignChannel) => {
      onSetValue([selectedNotificationKey, channelName], buildDefaultNotificationChannel());
    };

    return (
      <div>
        <Card className="mb-4" style={adminCardStyle}>
          <Card.Header style={adminCardHeaderStyle}>
            <div className="d-flex flex-wrap justify-content-between align-items-center">
              <div className="d-flex align-items-center mb-2">
                <div>
                  <strong className="d-block">{humanizeKey(selectedNotificationKey)}</strong>
                  <code className="small">{selectedNotificationKey}</code>
                </div>
              </div>
              <div className="d-flex align-items-center mb-2">
                <Form.Check
                  id={`${selectedNotificationKey}-selected-root-send`}
                  type="switch"
                  label="Notification enabled"
                  checked={selectedEntry.send !== false}
                  onChange={() => onSetValue([selectedNotificationKey, 'send'], selectedEntry.send === false)}
                  className="mr-3"
                />
                <span className={`badge badge-${isCampaignTemplate ? 'primary' : 'secondary'} mr-2`}>
                  {isCampaignTemplate ? 'CAMPAIGN' : 'GENERAL'}
                </span>
                {email ? <span className="badge badge-info mr-2">EMAIL</span> : null}
                {push ? <span className="badge badge-success mr-2">PUSH</span> : null}
                <Button
                  size="sm"
                  variant={selectedEditMode ? 'primary' : 'outline-primary'}
                  onClick={() => setSelectedEditMode((current) => !current)}
                >
                  {selectedEditMode ? 'Close edit mode' : 'Edit mode'}
                </Button>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <div
              className="rounded px-3 py-2 mb-3"
              style={{ background: '#f8faf9', border: '1px solid #edf1ee' }}
            >
              <div className="small text-muted">
                Email source: <strong>{getEmailContentSource(email)}</strong>
                <span className="mx-2">/</span>
                Localized: <strong>{countEmailLocalizations(email)}</strong>
                {rootCopyPreview ? (
                  <>
                    <span className="mx-2">/</span>
                    Root copy: {rootCopyPreview}
                  </>
                ) : null}
              </div>
            </div>

            <Row>
              <Col xl={6} className="mb-3">
                <NotificationCompactChannelPreview
                  notificationKey={selectedNotificationKey}
                  channelName="push"
                  channel={push}
                  editMode={selectedEditMode}
                  languages={languages}
                  onSetValue={onSetValue}
                  onAddChannel={() => handleAddSelectedChannel('push')}
                />
              </Col>
              <Col xl={6} className="mb-3">
                <NotificationCompactChannelPreview
                  notificationKey={selectedNotificationKey}
                  channelName="email"
                  channel={email}
                  editMode={selectedEditMode}
                  languages={languages}
                  onSetValue={onSetValue}
                  onAddChannel={() => handleAddSelectedChannel('email')}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <Card className="mb-4" style={adminCardStyle}>
      <Card.Header style={adminCardHeaderStyle}>
        <div className="d-flex flex-wrap justify-content-between align-items-center">
          <div className="mb-2">
            <strong>Notification Templates</strong>
            <div className="small text-muted">
              Search and open a template to edit channels, roles, languages, variables, and preview.
            </div>
          </div>
          <div className="d-flex flex-wrap mb-2">
            <Form.Control
              size="sm"
              placeholder="Search templates"
              value={filter}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFilter(event.target.value)}
              className="mr-2"
              style={{ width: 240 }}
            />
            <Form.Control
              as="select"
              size="sm"
              value={streamFilter}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setStreamFilter(event.target.value as 'all' | 'campaign' | 'general')}
              style={{ width: 150 }}
            >
              <option value="all">All templates</option>
              <option value="campaign">Campaign</option>
              <option value="general">General</option>
            </Form.Control>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        <Table responsive hover className="mb-0">
          <thead>
            <tr>
              <th className="pl-4">Template</th>
              <th>Type</th>
              <th>Channels</th>
              <th>Email source</th>
              <th>Localizations</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {visibleKeys.map((notificationKey) => {
              const entry = data[notificationKey] as Record<string, JsonValue>;
              const email = isRecord(entry.email) ? entry.email : undefined;
              const push = isRecord(entry.push) ? entry.push : undefined;
              const hasPreview = Boolean(
                (push && getNotificationPreviewOptions('push', push).length)
                || (email && getNotificationPreviewOptions('email', email).length),
              );

              return (
                <tr key={notificationKey}>
                  <td className="pl-4 align-middle">
                    <strong className="d-block">{humanizeKey(notificationKey)}</strong>
                    <code className="small">{notificationKey}</code>
                  </td>
                  <td className="align-middle">
                    <span className={`badge badge-${isCampaignTemplateKey(notificationKey) ? 'primary' : 'secondary'}`}>
                      {isCampaignTemplateKey(notificationKey) ? 'Campaign' : 'General'}
                    </span>
                  </td>
                  <td className="align-middle">
                    {email ? <span className="badge badge-info mr-1">Email</span> : null}
                    {push ? <span className="badge badge-success">Push</span> : null}
                  </td>
                  <td className="align-middle">{getEmailContentSource(email)}</td>
                  <td className="align-middle">{countEmailLocalizations(email)}</td>
                  <td className="align-middle">
                    <span className={`text-${entry.send === false ? 'muted' : 'success'}`}>
                      {entry.send === false ? 'Disabled' : 'Enabled'}
                    </span>
                  </td>
                  <td className="pr-4 text-right align-middle">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="mr-2"
                      style={{ width: 34, paddingLeft: 0, paddingRight: 0 }}
                      disabled={!hasPreview}
                      title="Mobile preview"
                      aria-label={`Preview ${humanizeKey(notificationKey)}`}
                      onMouseEnter={() => setHoveredNotificationKey(notificationKey)}
                      onMouseLeave={() => setHoveredNotificationKey(undefined)}
                      onFocus={() => setHoveredNotificationKey(notificationKey)}
                      onBlur={() => setHoveredNotificationKey(undefined)}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => onSelectNotification(notificationKey)}
                    >
                      Open editor
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {visibleKeys.length === 0 ? (
          <Alert variant="light" className="m-3 mb-3">No templates match this filter.</Alert>
        ) : null}
      </Card.Body>
      <Card.Footer className="small text-muted">
        Showing {visibleKeys.length} of {notificationKeys.length} templates
      </Card.Footer>
      {hoveredNotificationKey && hoveredEntry ? (
        <NotificationListHoverPreview
          notificationKey={hoveredNotificationKey}
          entry={hoveredEntry}
        />
      ) : null}
    </Card>
  );
};

export const AdminAppConfig: React.FC = () => {
  const initialHashState = useMemo(() => readAppConfigHash(), []);
  const { data, loading, error, refetch } = useQuery<AppConfigsQueryData>(APP_CONFIGS_QUERY);
  const [updateAppConfig, { loading: saving }] = useMutation<UpdateAppConfigResponse, UpdateAppConfigVariables>(
    UPDATE_APP_CONFIG_MUTATION,
  );
  const [selectedKey, setSelectedKey] = useState<string | undefined>(initialHashState.configKey);
  const [selectedNotificationKey, setSelectedNotificationKey] = useState<string | undefined>(
    initialHashState.notificationKey,
  );
  const [draftText, setDraftText] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [saveError, setSaveError] = useState<string | undefined>();
  const [saveMessage, setSaveMessage] = useState<string | undefined>();
  const [emailLanguages, setEmailLanguages] = useState<EmailLanguageOption[]>(
    () => FALLBACK_EMAIL_LANGUAGES.map((code) => ({ code, label: code.toUpperCase() })),
  );

  const configs = useMemo(
    () => (data?.appConfigs ?? []).slice().sort((first, second) => first.key.localeCompare(second.key)),
    [data],
  );

  const selectedConfig = useMemo(
    () => configs.find((config) => config.key === selectedKey) || configs[0],
    [configs, selectedKey],
  );

  useEffect(() => {
    if (configs.length === 0) return;
    if (selectedKey && configs.some((config) => config.key === selectedKey)) return;

    const defaultKey = configs[0].key;
    setSelectedKey(defaultKey);
    setSelectedNotificationKey(undefined);
    writeAppConfigHash(defaultKey, undefined, true);
  }, [configs, selectedKey]);

  useEffect(() => {
    const syncSelectionFromHash = () => {
      const hashState = readAppConfigHash();
      setSelectedKey(hashState.configKey);
      setSelectedNotificationKey(hashState.notificationKey);
    };

    window.addEventListener('hashchange', syncSelectionFromHash);
    window.addEventListener('popstate', syncSelectionFromHash);

    return () => {
      window.removeEventListener('hashchange', syncSelectionFromHash);
      window.removeEventListener('popstate', syncSelectionFromHash);
    };
  }, []);

  useEffect(() => {
    if (!selectedConfig) return;

    const nextText = formatJson(selectedConfig.data);
    setDraftText(nextText);
    setOriginalText(nextText);
    setSaveError(undefined);
    setSaveMessage(undefined);
  }, [selectedConfig?.id, selectedConfig?.key, selectedConfig?.updatedAt]);

  useEffect(() => {
    const controller = new AbortController();
    fetch('https://ultrainapp.com/static/locales/list', { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Language list request failed: ${response.status}`);
        return response.json();
      })
      .then((payload: JsonValue) => {
        const codes = isRecord(payload?.languages)
          ? Object.values(payload.languages).filter((value): value is string => typeof value === 'string')
          : [];
        const uniqueCodes = Array.from(new Set(['en', ...codes.map((code) => code.toLowerCase())]));
        setEmailLanguages(uniqueCodes.map((code) => ({ code, label: code.toUpperCase() })));
      })
      .catch((languageError: unknown) => {
        if (languageError instanceof Error && languageError.name === 'AbortError') return;
        console.error('Failed to load email languages:', languageError);
      });

    return () => controller.abort();
  }, []);

  const parsedDraft = useMemo(() => parseJson(draftText), [draftText]);
  const hasChanges = draftText !== originalText;

  const handleSelectConfig = (key: string) => {
    setSelectedKey(key);
    setSelectedNotificationKey(undefined);
    writeAppConfigHash(key);
  };

  const handleSelectNotification = (notificationKey?: string) => {
    setSelectedNotificationKey(notificationKey);
    writeAppConfigHash('notification', notificationKey);
  };

  const handleFormatJson = () => {
    const parsed = parseJson(draftText);
    if (parsed.error) {
      setSaveError(parsed.error);
      return;
    }
    setDraftText(formatJson(parsed.data));
    setSaveError(undefined);
  };

  const handleReset = () => {
    setDraftText(originalText);
    setSaveError(undefined);
    setSaveMessage(undefined);
  };

  const handleSetValue = (path: string[], value: JsonValue) => {
    const parsed = parseJson(draftText);
    if (parsed.error) {
      setSaveError(parsed.error);
      return;
    }

    const nextData = cloneJson(parsed.data);
    if (!setValueAtPath(nextData, path, value)) {
      setSaveError('Selected config is not an editable JSON object.');
      return;
    }

    setDraftText(formatJson(nextData));
    setSaveError(undefined);
    setSaveMessage(undefined);
  };

  const handleReplaceData = (nextData: JsonValue) => {
    setDraftText(formatJson(nextData));
    setSaveError(undefined);
    setSaveMessage(undefined);
  };

  const handleSave = async () => {
    if (!selectedConfig) return;

    const parsed = parseJson(draftText);
    if (parsed.error) {
      setSaveError(parsed.error);
      return;
    }
    const templateErrors = validateLocalizedEmailContent(parsed.data);
    if (templateErrors.length) {
      setSaveError(templateErrors.slice(0, 5).join('\n'));
      return;
    }

    try {
      const response = await updateAppConfig({
        variables: {
          input: {
            key: selectedConfig.key,
            data: parsed.data,
            archived: selectedConfig.archived,
          },
        },
      });

      const savedConfig = response.data?.updateAppConfig;
      if (savedConfig) {
        const nextText = formatJson(savedConfig.data);
        setDraftText(nextText);
        setOriginalText(nextText);
        setSelectedKey(savedConfig.key);
      }

      await refetch();
      setSaveError(undefined);
      setSaveMessage(`${selectedConfig.key} saved`);
    } catch (mutationError: unknown) {
      setSaveError(mutationError instanceof Error ? mutationError.message : String(mutationError));
      setSaveMessage(undefined);
    }
  };

  const rightItem = selectedConfig ? (
    <div className="d-flex align-items-center">
      <Button
        variant="outline-secondary"
        className="mr-2"
        onClick={handleFormatJson}
        disabled={Boolean(parsedDraft.error) || saving}
      >
        Format JSON
      </Button>
      <Button
        variant="outline-secondary"
        className="mr-2"
        onClick={handleReset}
        disabled={!hasChanges || saving}
      >
        Reset
      </Button>
      <Button
        variant="primary"
        onClick={handleSave}
        disabled={!hasChanges || Boolean(parsedDraft.error) || saving}
      >
        {saving ? 'Saving...' : 'Save'}
      </Button>
    </div>
  ) : undefined;

  return (
    <div>
      <PageHeader
        title="App Config"
        subtitle={selectedConfig ? selectedConfig.key : undefined}
        rightItem={rightItem}
      />

      {error && <Alert variant="danger">{error.message}</Alert>}
      {saveError && <Alert variant="danger">{saveError}</Alert>}
      {saveMessage && <Alert variant="success">{saveMessage}</Alert>}

      {loading && <Alert variant="info">Loading app config...</Alert>}

      {!loading && configs.length === 0 && (
        <Alert variant="warning">No active app config records found.</Alert>
      )}

      {selectedConfig && (
        <>
          <Card className="mb-4" style={adminCardStyle}>
            <Card.Header style={adminCardHeaderStyle}>
              <strong>Keys</strong>
            </Card.Header>
            <Card.Body className="d-flex flex-wrap pb-2">
              {configs.map((config) => (
                <Button
                  key={config.key}
                  variant={config.key === selectedConfig.key ? 'primary' : 'outline-secondary'}
                  onClick={() => handleSelectConfig(config.key)}
                  className="mr-2 mb-2 text-left"
                  style={{ minWidth: 220, maxWidth: 320 }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <strong>{config.key}</strong>
                    <span className="small ml-2">{getPayloadSize(config.data)}</span>
                  </div>
                  <div
                    className={config.key === selectedConfig.key ? 'small text-white-50' : 'small text-muted'}
                  >
                    {getDataType(config.data)} - {formatDate(config.updatedAt)}
                  </div>
                </Button>
              ))}
            </Card.Body>
          </Card>

          <Row>
            <Col lg={12}>
              {selectedConfig.key === 'notification_policy' && !parsedDraft.error && (
                <NotificationPolicyEditor data={parsedDraft.data} onSetValue={handleSetValue} />
              )}

              {selectedConfig.key === 'notification' && !parsedDraft.error && (
                <NotificationTemplateManager
                  data={parsedDraft.data}
                  onSetValue={handleSetValue}
                  languages={emailLanguages}
                  selectedNotificationKey={selectedNotificationKey}
                  onSelectNotification={handleSelectNotification}
                />
              )}

              {selectedConfig.key !== 'notification'
                && selectedConfig.key !== 'notification_policy'
                && hasNotificationShape(parsedDraft.data)
                && !parsedDraft.error && (
                <NotificationToggleEditor
                  data={parsedDraft.data}
                  onSetValue={handleSetValue}
                  languages={emailLanguages}
                />
              )}

              <details className="mb-4">
                <summary className="border rounded p-3 bg-white" style={{ cursor: 'pointer' }}>
                  <strong>Advanced JSON</strong>
                  {hasChanges ? <span className="small text-warning ml-2">Unsaved changes</span> : null}
                </summary>
                <Card className="border-top-0" style={adminCardStyle}>
                  <Card.Body>
                    <Form.Control
                      as="textarea"
                      rows={24}
                      value={draftText}
                      isInvalid={Boolean(parsedDraft.error)}
                      onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        setDraftText(event.target.value);
                        setSaveError(undefined);
                        setSaveMessage(undefined);
                      }}
                      style={{
                        fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                        fontSize: 13,
                        lineHeight: 1.45,
                        minHeight: 520,
                      }}
                    />
                    {parsedDraft.error && (
                      <div className="invalid-feedback d-block">
                        {parsedDraft.error}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </details>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};
